import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { analytics, WrapperStyles, getUsername } from "../../shared/utils";
import type { FBUser, FBMixtape } from "../../shared/types";
import {
  fetchUser,
  fetchMixtape,
  fetchMixtapeOwner,
} from "../../shared/services";
import DownloadOverlay from "../overlays/DownloadOverlay";
import FooterOverlay from "../overlays/FooterOverlay";
import LoadingOverlay from "../overlays/LoadingOverlay";
import DynamicMeta from "../shared/DynamicMeta";
import { HOOPER_NEON_GREEN } from "../../shared/constants";

const PublicMixtapeView = () => {
  const { id: mixtapeId } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [mixtape, setMixtape] = useState<FBMixtape>();
  const [user, setUser] = useState<FBUser>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch referrer id if passed
  const [searchParams] = useSearchParams();
  const referrerId = searchParams.get("referrerId");

  useEffect(() => {
    analytics.screen("Web/Public/Mixtape", {
      mixtapeId: mixtapeId,
      referrerId: referrerId,
    });
  }, [mixtapeId, referrerId]);

  useEffect(() => {
    if (!mixtapeId) {
      setLoading(false);
      return;
    }
    setLoading(true);

    // Conditionally fetch user based on referrerId
    const userPromise = referrerId
      ? fetchUser(referrerId)
      : fetchMixtapeOwner(mixtapeId);

    Promise.all([fetchMixtape(mixtapeId), userPromise])
      .then(([mixtape, user]) => {
        setMixtape(mixtape);
        setUser(user);
      })
      .catch((error) => {
        console.error(error);
        setUser(undefined);
        setMixtape(undefined);
      })
      .finally(() => setLoading(false));
  }, [mixtapeId, referrerId]);

  const MixtapeOverlay = () => {
    if (!user || !mixtape) return null;
    const name = getUsername(user);
    return (
      <Card
        sx={{
          display: "flex",
          backgroundColor: "rgba(0, 0, 0, 0.87)",
          alignItems: "center",
        }}
      >
        <CardContent sx={{ flex: "1 0 auto" }}>
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
            }}
          >
            {user.profilePicture && (
              <Avatar
                src={user.profilePicture}
                alt={name}
                sx={{ width: 24, height: 24 }}
              />
            )}
            <Typography
              sx={{
                color: "white",
                fontSize: !isMobile ? 18 : 16,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              {name} shared a mixtape
            </Typography>
          </Box>
          <Typography
            sx={{
              textAlign: "center",
              color: HOOPER_NEON_GREEN,
              fontSize: !isMobile ? 18 : 16,
              fontWeight: "bold",
            }}
          >
            Watch it on the app
          </Typography>
        </CardContent>
        {mixtape.highlights && mixtape.highlights.length > 0 && (
          <CardMedia
            component="img"
            sx={{
              width: 151,
              height: 151, // Make it square
              objectFit: "cover", // Crop to fill the square
              objectPosition: "center", // Center the crop
            }}
            image={mixtape.highlights[0].preview}
            alt="Mixtape avatar"
          />
        )}
      </Card>
    );
  };

  const getMetaTitle = () => {
    if (!mixtape) return "Try Hooper today - download from the app store";
    const name = (user && getUsername(user)) || "Someone";
    const titlePart = mixtape.title ? ` ${mixtape.title}` : "";
    return `${name}'s${titlePart} Mixtape on Hooper`;
  };

  const getMetaDescription = () => {
    if (!mixtape) return "Create your stats and highlights, shot by shot";
    const name = (user && getUsername(user)) || "someone";
    const titlePart = mixtape.title ? ` "${mixtape.title}"` : "";
    return `Watch ${name}'s${titlePart} mixtape on Hooper. See their best basketball highlights and moments.`;
  };

  const getMetaImage = () => {
    if (mixtape?.highlights?.[0]?.preview) return mixtape.highlights[0].preview;
    if (user?.profilePicture) return user.profilePicture;
    return "/logos/logo-white.svg";
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: "100vh",
        flexDirection: "column",
        display: "flex",
        px: "0 !important",
      }}
    >
      <DynamicMeta
        title={getMetaTitle()}
        description={getMetaDescription()}
        image={getMetaImage()}
        type="article"
        url={`/mixtape/${mixtapeId}${referrerId ? `?referrerId=${referrerId}` : ''}`}
      />
      <Box sx={WrapperStyles}>
        <Box style={{ marginTop: 100 }} />
        {loading ? (
          <LoadingOverlay />
        ) : (
          <DownloadOverlay
            parentViewName="Mixtape"
            backgroundId="background-3.jpg"
            deepLinkPath={`/mixtape/${mixtapeId}${referrerId ? `?referrerId=${referrerId}` : ""}`}
            children={<MixtapeOverlay />}
          />
        )}
        <Box sx={{ my: 8 }} />
        <FooterOverlay />
      </Box>
    </Container>
  );
};

export default PublicMixtapeView;
